'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import ParticleCanvas from '@/components/ui/ParticleCanvas';
import MagneticButton from '@/components/ui/MagneticButton';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const cards = document.querySelectorAll('.spotlight-card');
    const handleMove = (e: MouseEvent) => {
      cards.forEach((card) => {
        const el = card as HTMLElement;
        const rect = el.getBoundingClientRect();
        el.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
        el.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
      });
    };
    document.addEventListener('mousemove', handleMove);
    return () => document.removeEventListener('mousemove', handleMove);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative h-[100dvh] flex flex-col justify-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F6FF 55%, #FFFFFF 100%)' }}
    >
      {/* Particle network canvas */}
      <ParticleCanvas count={75} />

      {/* Grid lines */}
      <div className="absolute inset-0 grid-lines opacity-10" />

      {/* Orbs */}
      <div
        className="orb absolute w-[700px] h-[700px] rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #0066FF 0%, transparent 65%)', top: '-150px', right: '-150px' }}
      />
      <div
        className="orb-2 absolute w-[500px] h-[500px] rounded-full opacity-15 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)', bottom: '-80px', left: '-80px' }}
      />

    

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        style={{ paddingTop: 'clamp(4rem, 10vh, 9rem)', paddingBottom: 'clamp(1rem, 4vh, 6rem)' }}>
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          {/* Left: Main copy */}
          <div className="lg:col-span-7 flex flex-col" style={{ gap: 'clamp(0.75rem, 2.5vh, 2rem)' }}>
            {/* Badge */}
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#0066FF]/30 bg-[#0066FF]/10 text-[11px] font-semibold tracking-widest text-[#0066FF] uppercase">
                <span className="relative w-1.5 h-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0066FF] opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#0066FF]" />
                </span>
                India's Mobility Operating System
              </span>
            </div>

            {/* Headline */}
            <div className="animate-on-scroll stagger-2">
              <h1 className="font-display leading-[1.08] text-gray-900 tracking-tight"
                style={{ fontSize: 'clamp(2rem, min(8vw, 7vh), 5.5rem)' }}>
                <span className="block font-light text-gray-900/90">Empowering Business</span>
                <span className="block font-semibold italic text-[#0066FF]">
                  owners.
                </span>
                <span className="block font-light text-gray-900/90">Building India's Mobility Operators Network.</span>
              </h1>
            </div>

            {/* Subtitle */}
            <div className="animate-on-scroll stagger-3">
              <p className="text-sm md:text-base text-gray-600 max-w-2xl leading-relaxed font-light">
Movio AI is a SaaS-based unified AI mobility platform enabling Tier 2 and Tier 3 MSMEs to grow their businesses under their own brand.     </p>
              <p className="mt-2 text-base md:text-lg text-[#0066FF] max-w-2xl leading-relaxed font-medium">
                Your Brand. Your Business. Our Technology.
              </p>
            </div>

            {/* CTAs */}
            <div className="animate-on-scroll stagger-4 flex flex-col sm:flex-row gap-4 pt-2">
              <MagneticButton>
                <Link
                  href="/demo-booking"
                  className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold bg-[#3B82F6] text-white hover:bg-[#2563EB] shadow-lg shadow-[#3B82F6]/30 hover:shadow-xl transition-all duration-300"
                >
                  Book a Demo
                  <Icon name="ArrowRightIcon" size={18} />
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <Link
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-[#0066FF] border-2 border-[#0066FF]/40 hover:border-[#0066FF]/80 hover:bg-[#0066FF]/5 transition-all duration-300"
                >
                  <Icon name="UserGroupIcon" size={16} />
                  Partner With Us
                </Link>
              </MagneticButton>
            </div>
          </div>

          {/* Right: Isometric illustration + stat cards */}
          <div className="lg:col-span-5 animate-on-scroll stagger-5 hidden lg:flex flex-col gap-4">
            <div className="float-card-1 relative">
              {/* Ambient glow behind illustration */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-72 h-72 rounded-full blur-3xl opacity-25"
                  style={{ background: 'radial-gradient(circle, #3B82F6 0%, transparent 65%)' }} />
              </div>
              <svg viewBox="0 0 440 370" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto relative z-10">
                <defs>
                  <linearGradient id="screenBg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#0066FF" />
                    <stop offset="100%" stopColor="#3B82F6" />
                  </linearGradient>
                  <linearGradient id="laptopBase" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(0,102,255,0.16)" />
                    <stop offset="100%" stopColor="rgba(0,102,255,0.06)" />
                  </linearGradient>
                  <filter id="iso-shadow" x="-10%" y="-10%" width="130%" height="150%">
                    <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="rgba(0,102,255,0.18)" />
                  </filter>
                  <filter id="card-glow" x="-15%" y="-15%" width="130%" height="130%">
                    <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="rgba(0,102,255,0.15)" />
                  </filter>
                </defs>

                {/* ── DECORATIVE DOTS ── */}
                <circle cx="22" cy="38" r="4" fill="rgba(0,102,255,0.22)" />
                <circle cx="418" cy="55" r="5" fill="rgba(0,102,255,0.18)" />
                <circle cx="10" cy="210" r="3" fill="rgba(0,102,255,0.16)" />
                <circle cx="428" cy="290" r="3.5" fill="rgba(0,102,255,0.16)" />
                <circle cx="60" cy="345" r="3" fill="rgba(0,102,255,0.14)" />
                <circle cx="390" cy="340" r="2.5" fill="rgba(0,102,255,0.14)" />
                <circle cx="200" cy="20" r="2.5" fill="rgba(0,102,255,0.16)" />

                {/* ── FLOATING CARD: EMAIL (top-left) ── */}
                <g transform="translate(8,48)" filter="url(#card-glow)">
                  <rect width="108" height="74" rx="13" fill="#FFFFFF" stroke="rgba(0,102,255,0.25)" strokeWidth="1" />
                  {/* Envelope */}
                  <rect x="12" y="13" width="34" height="24" rx="5" fill="none" stroke="#3B82F6" strokeWidth="1.8" />
                  <polyline points="12,16 29,27 46,16" stroke="#3B82F6" strokeWidth="1.8" fill="none" strokeLinecap="round" />
                  {/* Green check badge */}
                  <circle cx="93" cy="16" r="10" fill="#10b981" />
                  <path d="M88,16 l4,4 l7,-7" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  {/* Text lines */}
                  <rect x="12" y="44" width="84" height="4" rx="2" fill="rgba(15,23,42,0.45)" />
                  <rect x="12" y="52" width="62" height="3.5" rx="1.75" fill="rgba(15,23,42,0.25)" />
                  <rect x="12" y="60" width="44" height="3.5" rx="1.75" fill="rgba(15,23,42,0.15)" />
                </g>

                {/* Connection line: email card → laptop */}
                <path d="M116,88 Q148,102 162,132" stroke="rgba(0,102,255,0.25)" strokeWidth="1.2" strokeDasharray="5,4" fill="none" />

                {/* ── FLOATING CARD: PROFILE (top-right) ── */}
                <g transform="translate(296,18)" filter="url(#card-glow)">
                  <rect width="132" height="90" rx="13" fill="#FFFFFF" stroke="rgba(0,102,255,0.25)" strokeWidth="1" />
                  {/* Avatar circle */}
                  <circle cx="26" cy="28" r="16" fill="rgba(59,130,246,0.25)" stroke="rgba(59,130,246,0.5)" strokeWidth="1.5" />
                  <circle cx="26" cy="24" r="8" fill="rgba(59,130,246,0.55)" />
                  <path d="M10,42 Q26,34 42,42" fill="rgba(59,130,246,0.35)" />
                  {/* Name & role */}
                  <rect x="52" y="16" width="68" height="5" rx="2.5" fill="rgba(15,23,42,0.6)" />
                  <rect x="52" y="25" width="48" height="4" rx="2" fill="rgba(15,23,42,0.3)" />
                  {/* Divider */}
                  <line x1="12" y1="50" x2="120" y2="50" stroke="rgba(0,102,255,0.14)" strokeWidth="1" />
                  {/* Info rows */}
                  <circle cx="20" cy="62" r="5" fill="rgba(0,102,255,0.5)" />
                  <rect x="30" y="59" width="82" height="3.5" rx="1.75" fill="rgba(15,23,42,0.3)" />
                  <circle cx="20" cy="76" r="5" fill="rgba(0,102,255,0.4)" />
                  <rect x="30" y="73" width="64" height="3.5" rx="1.75" fill="rgba(15,23,42,0.2)" />
                </g>

                {/* Connection line: profile card → laptop */}
                <path d="M296,64 Q275,85 263,110" stroke="rgba(0,102,255,0.25)" strokeWidth="1.2" strokeDasharray="5,4" fill="none" />

                {/* ── FLOATING CARD: ANALYTICS (right-mid) ── */}
                <g transform="translate(312,200)" filter="url(#card-glow)">
                  <rect width="118" height="96" rx="13" fill="#FFFFFF" stroke="rgba(0,102,255,0.25)" strokeWidth="1" />
                  <rect x="10" y="10" width="64" height="5" rx="2.5" fill="rgba(15,23,42,0.5)" />
                  <rect x="10" y="19" width="42" height="3.5" rx="1.75" fill="rgba(15,23,42,0.25)" />
                  {/* Bar chart */}
                  <line x1="10" y1="82" x2="108" y2="82" stroke="rgba(0,102,255,0.18)" strokeWidth="1" />
                  <rect x="12" y="62" width="14" height="20" rx="2.5" fill="rgba(0,102,255,0.45)" />
                  <rect x="30" y="52" width="14" height="30" rx="2.5" fill="rgba(0,102,255,0.6)" />
                  <rect x="48" y="43" width="14" height="39" rx="2.5" fill="#FBBF24" />
                  <rect x="66" y="55" width="14" height="27" rx="2.5" fill="rgba(59,130,246,0.55)" />
                  <rect x="84" y="48" width="14" height="34" rx="2.5" fill="rgba(0,102,255,0.5)" />
                </g>

                {/* Connection line: analytics card → laptop */}
                <path d="M312,240 Q290,238 274,228" stroke="rgba(0,102,255,0.25)" strokeWidth="1.2" strokeDasharray="5,4" fill="none" />

                {/* ══════════════════════════════════
                    LAPTOP — isometric style
                ══════════════════════════════════ */}

                {/* Keyboard base — top face (isometric top) */}
                <polygon
                  points="118,282 278,236 308,262 148,308"
                  fill="url(#laptopBase)"
                  stroke="rgba(0,102,255,0.28)"
                  strokeWidth="1"
                  filter="url(#iso-shadow)"
                />
                {/* Keyboard base — right side face */}
                <polygon
                  points="278,236 308,262 308,272 278,246"
                  fill="rgba(0,102,255,0.1)"
                  stroke="rgba(0,102,255,0.16)"
                  strokeWidth="0.5"
                />
                {/* Keyboard surface detail */}
                <polygon points="128,286 268,244 295,266 155,308" fill="rgba(0,102,255,0.06)" />
                {/* Trackpad */}
                <polygon points="188,289 238,274 248,280 198,295" fill="rgba(0,102,255,0.12)" stroke="rgba(0,102,255,0.18)" strokeWidth="0.5" />

                {/* Hinge bar */}
                <polygon points="118,276 278,230 280,236 120,282" fill="rgba(0,102,255,0.35)" stroke="rgba(0,102,255,0.35)" strokeWidth="0.5" />

                {/* Laptop screen — back panel */}
                <polygon
                  points="118,276 278,230 278,108 118,154"
                  fill="rgba(0,50,150,0.95)"
                  stroke="rgba(0,102,255,0.3)"
                  strokeWidth="1"
                  filter="url(#iso-shadow)"
                />
                {/* Laptop screen — face */}
                <polygon
                  points="126,268 270,224 270,116 126,160"
                  fill="url(#screenBg)"
                />

                {/* ── SCREEN UI ── */}
                {/* Top bar */}
                <polygon points="126,160 270,116 270,133 126,177" fill="rgba(0,50,150,0.55)" />
                {/* Three dots */}
                <circle cx="136" cy="169" r="3.5" fill="rgba(255,255,255,0.65)" />
                <circle cx="147" cy="166" r="3.5" fill="rgba(255,255,255,0.45)" />
                <circle cx="158" cy="163" r="3.5" fill="rgba(255,255,255,0.28)" />
                {/* Nav links */}
                <polygon points="176,173 196,167 196,170 176,176" fill="rgba(255,255,255,0.5)" />
                <polygon points="202,171 218,166 218,169 202,174" fill="rgba(255,255,255,0.3)" />
                <polygon points="223,169 240,164 240,167 223,172" fill="rgba(255,255,255,0.3)" />

                {/* Sidebar strip */}
                <polygon points="126,177 150,170 150,268 126,275" fill="rgba(0,40,130,0.5)" />
                {/* Sidebar icons (small squares) */}
                <rect x="131" y="178" width="5" height="5" rx="1" fill="#FBBF24" transform="skewY(-12)" />
                <rect x="131" y="190" width="5" height="5" rx="1" fill="rgba(255,255,255,0.3)" transform="skewY(-12)" />
                <rect x="131" y="202" width="5" height="5" rx="1" fill="rgba(255,255,255,0.3)" transform="skewY(-12)" />
                <rect x="131" y="214" width="5" height="5" rx="1" fill="rgba(255,255,255,0.3)" transform="skewY(-12)" />
                <rect x="131" y="226" width="5" height="5" rx="1" fill="rgba(255,255,255,0.3)" transform="skewY(-12)" />

                {/* KPI tiles row */}
                <polygon points="156,175 193,164 193,186 156,197" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.16)" strokeWidth="0.5" />
                <polygon points="197,163 230,153 230,174 197,184" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.16)" strokeWidth="0.5" />
                <polygon points="234,152 262,143 262,163 234,172" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.16)" strokeWidth="0.5" />

                {/* Chart area */}
                <polygon points="156,202 268,167 268,224 156,259" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
                {/* Chart bars (skewed to match laptop face angle) */}
                <polygon points="162,248 170,245 170,226 162,229" fill="rgba(255,255,255,0.5)" />
                <polygon points="174,244 182,241 182,218 174,221" fill="rgba(255,255,255,0.62)" />
                <polygon points="186,240 194,237 194,210 186,213" fill="#FBBF24" />
                <polygon points="198,236 206,233 206,218 198,221" fill="rgba(255,255,255,0.52)" />
                <polygon points="210,232 218,229 218,213 210,216" fill="rgba(255,255,255,0.46)" />
                <polygon points="222,228 230,225 230,216 222,219" fill="rgba(255,255,255,0.4)" />

                {/* Service icon tiles at bottom of screen */}
                <polygon points="156,262 183,253 183,268 156,277" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.28)" strokeWidth="0.5" />
                <polygon points="186,252 210,244 210,258 186,266" fill="rgba(251,191,36,0.35)" stroke="rgba(251,191,36,0.5)" strokeWidth="0.5" />
                <polygon points="213,243 237,235 237,249 213,257" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.28)" strokeWidth="0.5" />

                {/* Camera dot on screen top */}
                <circle cx="198" cy="114" r="3.5" fill="rgba(255,255,255,0.22)" />

                {/* ══════════════════════════════════
                    MOBILE APP MOCKUP (left side)
                ══════════════════════════════════ */}

                {/* Phone glow */}
                <ellipse cx="70" cy="240" rx="48" ry="90" fill="rgba(0,102,255,0.08)" />

                {/* Phone shadow */}
                <ellipse cx="70" cy="318" rx="40" ry="10" fill="rgba(0,102,255,0.12)" />

                {/* Connection line: phone → laptop */}
                <path d="M112,225 Q136,232 158,258" stroke="rgba(0,102,255,0.25)" strokeWidth="1.2" strokeDasharray="5,4" fill="none" />

                {/* Phone outer frame */}
                <rect x="20" y="130" width="92" height="178" rx="18" fill="rgba(0,40,130,0.95)" stroke="rgba(0,102,255,0.3)" strokeWidth="1.5" filter="url(#iso-shadow)" />

                {/* Side buttons */}
                <rect x="17" y="170" width="4" height="22" rx="2" fill="rgba(255,255,255,0.22)" />
                <rect x="17" y="198" width="4" height="14" rx="2" fill="rgba(255,255,255,0.22)" />
                <rect x="111" y="178" width="4" height="18" rx="2" fill="rgba(255,255,255,0.22)" />

                {/* Notch */}
                <rect x="46" y="130" width="40" height="8" rx="4" fill="rgba(0,0,0,0.45)" />

                {/* Screen clipping region — everything inside is clipped to rounded rect */}
                <defs>
                  <clipPath id="phoneClip">
                    <rect x="26" y="146" width="80" height="154" rx="12" />
                  </clipPath>
                </defs>

                {/* Screen background */}
                <rect x="26" y="146" width="80" height="154" rx="12" fill="rgba(0,45,140,0.98)" />

                <g clipPath="url(#phoneClip)">
                  {/* Header bar */}
                  <rect x="26" y="146" width="80" height="24" fill="rgba(0,102,255,0.85)" />
                  {/* App name */}
                  <rect x="38" y="151" width="44" height="5" rx="2.5" fill="rgba(255,255,255,0.9)" />
                  <rect x="44" y="159" width="32" height="3.5" rx="1.75" fill="rgba(255,255,255,0.55)" />

                  {/* "Our Platforms" label */}
                  <rect x="30" y="177" width="36" height="3.5" rx="1.75" fill="rgba(255,255,255,0.45)" />

                  {/* Service grid 3×2 */}
                  {/* Row 1 */}
                  <rect x="30" y="185" width="20" height="20" rx="6" fill="rgba(0,102,255,0.55)" />
                  <rect x="54" y="185" width="20" height="20" rx="6" fill="rgba(251,191,36,0.55)" />
                  <rect x="78" y="185" width="20" height="20" rx="6" fill="rgba(59,130,246,0.55)" />
                  {/* Row 1 icons (simplified dots) */}
                  <circle cx="40" cy="195" r="5" fill="rgba(255,255,255,0.4)" />
                  <circle cx="64" cy="195" r="5" fill="rgba(255,255,255,0.4)" />
                  <circle cx="88" cy="195" r="5" fill="rgba(255,255,255,0.4)" />

                  {/* Row 2 */}
                  <rect x="30" y="209" width="20" height="20" rx="6" fill="rgba(59,130,246,0.5)" />
                  <rect x="54" y="209" width="20" height="20" rx="6" fill="rgba(0,102,255,0.5)" />
                  <rect x="78" y="209" width="20" height="20" rx="6" fill="rgba(251,191,36,0.5)" />
                  <circle cx="40" cy="219" r="5" fill="rgba(255,255,255,0.4)" />
                  <circle cx="64" cy="219" r="5" fill="rgba(255,255,255,0.4)" />
                  <circle cx="88" cy="219" r="5" fill="rgba(255,255,255,0.4)" />

                  {/* Grid labels */}
                  <rect x="31" y="229" width="18" height="2.5" rx="1.25" fill="rgba(255,255,255,0.3)" />
                  <rect x="55" y="229" width="18" height="2.5" rx="1.25" fill="rgba(255,255,255,0.3)" />
                  <rect x="79" y="229" width="18" height="2.5" rx="1.25" fill="rgba(255,255,255,0.3)" />

                  {/* Book Now button */}
                  <rect x="30" y="237" width="72" height="16" rx="8" fill="#FBBF24" />
                  <rect x="44" y="242" width="44" height="4" rx="2" fill="rgba(15,23,42,0.6)" />

                  {/* Recent activity card */}
                  <rect x="30" y="258" width="72" height="28" rx="7" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.14)" strokeWidth="0.5" />
                  <circle cx="40" cy="267" r="6" fill="rgba(16,185,129,0.7)" />
                  <path d="M37,267 l2.5,2.5 l5,-5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  <rect x="52" y="262" width="42" height="3.5" rx="1.75" fill="rgba(255,255,255,0.55)" />
                  <rect x="52" y="269" width="30" height="3" rx="1.5" fill="rgba(255,255,255,0.3)" />
                  <rect x="30" y="278" width="72" height="2.5" rx="1.25" fill="rgba(255,255,255,0.08)" />

                  {/* Bottom nav */}
                  <rect x="26" y="286" width="80" height="14" fill="rgba(0,30,110,0.85)" />
                  <circle cx="46" cy="293" r="3.5" fill="rgba(255,255,255,0.8)" />
                  <circle cx="66" cy="293" r="3.5" fill="rgba(255,255,255,0.32)" />
                  <circle cx="86" cy="293" r="3.5" fill="rgba(255,255,255,0.32)" />
                </g>

                {/* Home indicator bar */}
                <rect x="46" y="302" width="40" height="4" rx="2" fill="rgba(255,255,255,0.26)" />

                {/* ── COFFEE CUP (bottom right of laptop) ── */}
                <g transform="translate(296, 272)">
                  <rect x="0" y="6" width="28" height="30" rx="5" fill="rgba(0,102,255,0.1)" stroke="rgba(0,102,255,0.28)" strokeWidth="1" />
                  {/* Handle */}
                  <path d="M28,14 Q40,14 40,21 Q40,28 28,28" stroke="rgba(0,102,255,0.28)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                  {/* Coffee */}
                  <rect x="4" y="14" width="20" height="18" rx="3" fill="rgba(120,60,20,0.55)" />
                  {/* Steam lines */}
                  <path d="M7,6 Q9,-2 7,-8" stroke="rgba(0,102,255,0.25)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                  <path d="M14,6 Q16,-4 14,-10" stroke="rgba(0,102,255,0.25)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                  <path d="M21,6 Q23,-2 21,-7" stroke="rgba(0,102,255,0.25)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                </g>

                {/* ── NOTIFICATION BADGE floating near laptop ── */}
                <g transform="translate(240,90)">
                  <rect width="80" height="32" rx="16" fill="rgba(16,185,129,0.15)" stroke="rgba(16,185,129,0.4)" strokeWidth="1" />
                  <circle cx="18" cy="16" r="6" fill="#10b981" />
                  <path d="M14,16 l3,3 l6,-6" stroke="white" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  <rect x="30" y="10" width="42" height="4" rx="2" fill="rgba(15,23,42,0.55)" />
                  <rect x="30" y="18" width="30" height="3.5" rx="1.75" fill="rgba(15,23,42,0.3)" />
                </g>

              </svg>
            </div>

            {/* Two stat cards */}
            <div className="float-card-2 grid grid-cols-2 gap-3">
              <div className="bg-[#0066FF]/[0.06] border border-[#0066FF]/[0.18] rounded-xl p-4 backdrop-blur-xl">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#0066FF] mb-1 font-semibold">Daily Trips</div>
                <div className="font-display text-3xl font-semibold text-gray-900 leading-none">
                  5,500<span className="text-sm text-gray-500 font-light">+</span>
                </div>
                <div className="mt-2 flex gap-0.5">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className="flex-1 h-1 rounded-sm"
                      style={{ background: i < 6 ? '#0066FF' : 'rgba(0,102,255,0.15)', transform: 'skewX(-6deg)' }} />
                  ))}
                </div>
              </div>
              <div className="bg-[#0066FF]/[0.06] border border-[#0066FF]/[0.18] rounded-xl p-4 backdrop-blur-xl">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#0066FF] mb-1 font-semibold">Cities Active</div>
                <div className="font-display text-3xl font-semibold text-gray-900 leading-none">
                  22<span className="text-sm text-gray-500 font-light ml-0.5">+</span>
                </div>
                <div className="mt-2 flex gap-1 flex-wrap">
                  {['TN', 'KL', 'KA', 'CG'].map(s => (
                    <span key={s} className="text-[9px] px-1.5 py-0.5 rounded bg-[#0066FF]/15 text-[#0066FF] font-semibold">{s}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Notification toast */}
            <div className="tech-node-1 bg-[#0066FF]/[0.06] border border-[#0066FF]/[0.18] rounded-xl px-4 py-3 backdrop-blur-xl flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center bg-[#FBBF24]/25">
                <Icon name="BoltIcon" size={16} className="text-[#B45309]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-gray-900 leading-tight">New booking confirmed</div>
                <div className="text-[10px] text-gray-500 mt-0.5">Chennai → Airport · Express</div>
              </div>
              <div className="text-[9px] text-gray-400 whitespace-nowrap">just now</div>
            </div>
          </div>
        </div>

        {/* Floating platform tags */}
        <div className="animate-on-scroll stagger-6 flex flex-wrap gap-3" style={{ marginTop: 'clamp(0.75rem, 3vh, 4rem)' }}>
          {['Mobile App', 'Admin Dashboard', 'WhatsApp Bot', 'Kiosk System', 'Ride Hailing', 'Route Optimizer', 'Corporate Rental'].map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0066FF]/[0.06] border border-[#0066FF]/[0.22] text-[12px] font-medium text-[#0066FF]/90 hover:text-[#0066FF] hover:border-[#0066FF]/50 hover:bg-[#0066FF]/[0.1] transition-all cursor-default"
            >
              <span className="w-1 h-1 rounded-full bg-[#0066FF]" />
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, #FFFFFF)' }} />
    </section>
  );
}